import { useState } from 'react';
import {
  Package,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  MessageSquare,
  FileText,
  ShoppingCart,
  AlertTriangle,
  Truck,
  Boxes,
  Tags,
  Percent,
  Wallet,
  BarChart3,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subMonths, startOfDay, endOfDay } from 'date-fns';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { useAuthStore } from '@/stores/auth-store';
import { DateRangePicker } from '@/components/shared/date-range-picker';

const statusColors: Record<string, string> = {
  // Inquiry statuses
  NEW: 'bg-blue-50 text-blue-700 border-blue-200',
  IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-200',
  QUOTED: 'bg-purple-50 text-purple-700 border-purple-200',
  WON: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  LOST: 'bg-red-50 text-red-700 border-red-200',
  // Order statuses
  DRAFT: 'bg-slate-50 text-slate-700 border-slate-200',
  CONFIRMED: 'bg-blue-50 text-blue-700 border-blue-200',
  PROCESSING: 'bg-amber-50 text-amber-700 border-amber-200',
  SHIPPED: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  DELIVERED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  COMPLETED: 'bg-green-50 text-green-700 border-green-200',
  CANCELLED: 'bg-red-50 text-red-700 border-red-200',
};

export function TradingDashboardPage() {
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation('trading');
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfDay(subMonths(new Date(), 1)),
    to: endOfDay(new Date()),
  });
  const { data, isLoading, error } = trpc.tradingDashboard.stats.useQuery({
    dateFrom: dateRange.from.toISOString(),
    dateTo: dateRange.to.toISOString(),
  });

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.greetingMorning');
    if (hour < 18) return t('dashboard.greetingAfternoon');
    return t('dashboard.greetingEvening');
  };

  // Get locale for date formatting
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <div className="relative">
          <div className="h-16 w-16 bg-gradient-to-br from-amber-500 to-amber-600 animate-pulse" />
          <Package className="absolute inset-0 m-auto h-8 w-8 text-white animate-bounce" />
        </div>
        <p className="mt-6 text-muted-foreground animate-pulse">{t('dashboard.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-2 border-destructive/20 bg-destructive/5 p-8">
        <p className="font-display text-lg font-semibold text-destructive">{t('dashboard.error')}</p>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  const stats = data?.stats;
  const recentInquiries = data?.recentInquiries ?? [];
  const recentOrders = data?.recentOrders ?? [];
  const lowStockItems = data?.lowStockItems ?? [];

  const statCards = [
    {
      key: t('dashboard.totalRevenue'),
      value: stats?.revenue.total ?? 0,
      change: stats?.revenue.change ?? 0,
      icon: DollarSign,
      format: 'currency' as const,
      gradient: 'from-emerald-500 to-teal-600',
      subtitle: `${formatCurrency(stats?.revenue.thisMonth ?? 0)} ${t('dashboard.thisMonth')}`,
    },
    {
      key: t('dashboard.grossProfit'),
      value: stats?.profit?.grossProfit ?? 0,
      change: stats?.profit?.change ?? 0,
      icon: Wallet,
      format: 'currency' as const,
      gradient: 'from-green-500 to-emerald-600',
      subtitle: `${formatCurrency(stats?.profit?.thisMonth ?? 0)} ${t('dashboard.thisMonth')}`,
    },
    {
      key: t('dashboard.profitMargin'),
      value: stats?.profit?.avgMargin ?? 0,
      change: 0,
      icon: Percent,
      format: 'percent' as const,
      gradient: 'from-teal-500 to-cyan-600',
      hideChange: true,
      subtitle: `${stats?.profit?.thisMonthMargin?.toFixed(1) ?? 0}% ${t('dashboard.thisMonth')}`,
    },
    {
      key: t('dashboard.inventoryValue'),
      value: stats?.inventory?.value ?? 0,
      change: 0,
      icon: BarChart3,
      format: 'currency' as const,
      gradient: 'from-amber-500 to-orange-600',
      hideChange: true,
      subtitle: `${stats?.inventory?.lowStockCount ?? 0} ${t('dashboard.lowStock')}`,
    },
  ];

  const secondaryStats = [
    {
      key: t('dashboard.salesOrders'),
      value: stats?.orders.thisMonth ?? 0,
      change: stats?.orders.change ?? 0,
      icon: ShoppingCart,
      format: 'number' as const,
      gradient: 'from-blue-500 to-indigo-600',
      subtitle: `${stats?.orders.pending ?? 0} ${t('dashboard.pending')}`,
    },
    {
      key: t('dashboard.newInquiries'),
      value: stats?.inquiries.new ?? 0,
      change: stats?.inquiries.change ?? 0,
      icon: MessageSquare,
      format: 'number' as const,
      gradient: 'from-violet-500 to-purple-600',
      subtitle: `${stats?.inquiries.thisMonth ?? 0} ${t('dashboard.thisMonth')}`,
    },
    {
      key: t('dashboard.cogs'),
      value: stats?.cogs?.total ?? 0,
      change: 0,
      icon: Package,
      format: 'currency' as const,
      hideChange: true,
      gradient: 'from-slate-500 to-slate-600',
      subtitle: t('dashboard.costOfGoodsSold'),
    },
    {
      key: t('dashboard.activeProducts'),
      value: stats?.products.total ?? 0,
      change: 0,
      icon: Package,
      format: 'number' as const,
      hideChange: true,
      gradient: 'from-rose-500 to-pink-600',
      subtitle: `${stats?.products.featured ?? 0} ${t('dashboard.featured')}`,
    },
  ];

  return (
    <div className="space-y-6 page-enter">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-600 to-amber-700 p-6 text-white">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-amber-900/30 to-transparent translate-y-1/2 -translate-x-1/2" />
        <Boxes className="absolute right-6 bottom-6 h-24 w-24 text-white/5" />

        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-amber-200">
                <Package className="h-4 w-4" />
                <span className="text-xs font-medium tracking-wide uppercase">{t('dashboard.title')}</span>
              </div>
              <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold tracking-tight">
                {getGreeting()}, {user?.firstName || 'there'}
              </h1>
              <p className="mt-1 text-sm text-white/70 max-w-xl">
                {t('dashboard.subtitle')}
              </p>
            </div>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const isIncrease = stat.change >= 0;
          return (
            <div
              key={stat.key}
              className="group bg-card border border-border p-4 hover:border-foreground/20 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className={`bg-gradient-to-br ${stat.gradient} p-2.5`}>
                  <stat.icon className="h-4 w-4 text-white" />
                </div>
                {!stat.hideChange && stat.change !== 0 && (
                  <div
                    className={`flex items-center gap-1 border px-2 py-0.5 text-xs font-medium ${
                      isIncrease
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}
                  >
                    {isIncrease ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {Math.abs(stat.change)}%
                  </div>
                )}
              </div>
              <div className="mt-4">
                <p className="font-display text-2xl font-semibold tracking-tight">
                  {stat.format === 'currency'
                    ? formatCurrency(stat.value)
                    : `${Number(stat.value).toFixed(1)}%`}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">{stat.key}</p>
                {stat.subtitle && (
                  <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Secondary Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {secondaryStats.map((stat) => {
          const isIncrease = stat.change >= 0;
          return (
            <div
              key={stat.key}
              className="group bg-card border border-border p-4 hover:border-foreground/20 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className={`bg-gradient-to-br ${stat.gradient} p-2`}>
                  <stat.icon className="h-4 w-4 text-white" />
                </div>
                {!stat.hideChange && stat.change !== 0 && (
                  <div
                    className={`flex items-center gap-1 border px-1.5 py-0.5 text-xs font-medium ${
                      isIncrease
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}
                  >
                    {isIncrease ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {Math.abs(stat.change)}%
                  </div>
                )}
              </div>
              <div className="mt-3">
                <p className="font-display text-xl font-semibold tracking-tight">
                  {stat.format === 'currency'
                    ? formatCurrency(stat.value)
                    : formatNumber(stat.value)}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">{stat.key}</p>
                {stat.subtitle && (
                  <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          {
            icon: Tags,
            value: stats?.catalog.brands ?? 0,
            label: t('dashboard.brands'),
            color: 'text-amber-600',
            bg: 'bg-amber-100',
          },
          {
            icon: Boxes,
            value: stats?.catalog.models ?? 0,
            label: t('dashboard.models'),
            color: 'text-blue-600',
            bg: 'bg-blue-100',
          },
          {
            icon: FileText,
            value: stats?.quotes.pending ?? 0,
            label: t('dashboard.pendingQuotes'),
            color: 'text-violet-600',
            bg: 'bg-violet-100',
          },
          {
            icon: Truck,
            value: stats?.purchasing.pendingOrders ?? 0,
            label: t('dashboard.pendingPOs'),
            color: 'text-emerald-600',
            bg: 'bg-emerald-100',
          },
        ].map((item) => (
          <div
            key={item.label}
            className="bg-card border border-border p-4 flex items-center gap-3 hover:border-foreground/20 transition-colors cursor-pointer"
          >
            <div className={`${item.bg} p-2.5`}>
              <item.icon className={`h-5 w-5 ${item.color}`} />
            </div>
            <div>
              <p className="font-display text-xl font-semibold">{item.value}</p>
              <p className="text-sm text-muted-foreground">{item.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="border-2 border-amber-200 bg-amber-50 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-100 p-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900">{t('dashboard.lowStockAlert')}</h3>
              <p className="text-sm text-amber-700">{t('dashboard.itemsBelowReorder', { count: lowStockItems.length })}</p>
            </div>
            <Link
              to="/trading/inventory"
              className="ml-auto text-sm font-medium text-amber-700 hover:text-amber-900 flex items-center gap-1"
            >
              {t('dashboard.viewInventory')} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {lowStockItems.slice(0, 6).map((item) => (
              <div key={item.id} className="flex items-center justify-between bg-white p-3 border border-amber-200">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{item.productName}</p>
                  <p className="text-xs text-muted-foreground">{item.warehouseName}</p>
                </div>
                <div className="text-right ml-2">
                  <p className="font-semibold text-amber-700">{item.quantity}</p>
                  <p className="text-xs text-muted-foreground">/ {item.reorderPoint}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Recent Inquiries */}
        <div className="bg-card border border-border overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div>
              <h2 className="font-display text-base font-semibold">{t('dashboard.recentInquiries')}</h2>
              <p className="text-xs text-muted-foreground">{t('dashboard.latestInquiries')}</p>
            </div>
            <Link
              to="/trading/inquiries"
              className="group flex items-center gap-1.5 h-8 bg-muted px-3 text-sm font-medium transition-colors hover:bg-amber-100 hover:text-amber-700"
            >
              {t('dashboard.viewAll')}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          {recentInquiries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-5">
              <div className="bg-muted p-5">
                <MessageSquare className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="mt-3 font-display text-base font-medium">{t('dashboard.noInquiriesYet')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.inquiriesWillAppear')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentInquiries.map((inquiry) => (
                <Link
                  key={inquiry.id}
                  to={`/trading/inquiries?id=${inquiry.id}`}
                  className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{inquiry.inquiryNumber}</span>
                      <span
                        className={`border px-2 py-0.5 text-xs font-medium ${
                          statusColors[inquiry.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        {inquiry.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground truncate">
                      {inquiry.companyName || inquiry.contactName} • {inquiry.itemCount} {t('dashboard.items')}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xs text-muted-foreground">
                      {new Date(inquiry.createdAt).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-card border border-border overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <div>
              <h2 className="font-display text-base font-semibold">{t('dashboard.recentOrders')}</h2>
              <p className="text-xs text-muted-foreground">{t('dashboard.latestOrders')}</p>
            </div>
            <Link
              to="/trading/sales/orders"
              className="group flex items-center gap-1.5 h-8 bg-muted px-3 text-sm font-medium transition-colors hover:bg-amber-100 hover:text-amber-700"
            >
              {t('dashboard.viewAll')}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-5">
              <div className="bg-muted p-5">
                <ShoppingCart className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="mt-3 font-display text-base font-medium">{t('dashboard.noOrdersYet')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.ordersWillAppear')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/trading/sales/orders?id=${order.id}`}
                  className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{order.orderNumber}</span>
                      <span
                        className={`border px-2 py-0.5 text-xs font-medium ${
                          statusColors[order.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground truncate">
                      {order.companyName || order.contactName} • {order.itemCount} {t('dashboard.items')}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-display text-base font-semibold">
                      {formatCurrency(order.total, order.currency)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
